/**
 * Deferred image loading for content that is laid out but not visible.
 *
 * `visibility: hidden`, `opacity: 0` and off-screen transforms all still produce layout
 * boxes, so vanilla-lazyload's IntersectionObserver happily loads every image inside a
 * closed nav dropdown, drawer or modal on first paint. Images rendered with
 * `manual_load: true` keep their `data-src` but drop the `lazy` class, which takes them
 * out of lazyload's hands entirely — this helper is what actually attaches them, and it
 * should be called when the containing panel is revealed.
 */

const ATTACHED_ATTRIBUTE = 'data-attached'

const DEFERRED_IMAGE_SELECTOR = `img[data-src]:not(.lazy):not([${ATTACHED_ATTRIBUTE}])`
const DEFERRED_SOURCE_SELECTOR = `source[data-srcset]:not([${ATTACHED_ATTRIBUTE}])`

type Root = ParentNode | null | undefined

function attachSource(element: HTMLImageElement | HTMLSourceElement) {
  const { src, srcset, sizes } = element.dataset

  if (element instanceof HTMLImageElement && !element.dataset.placeholder) {
    const placeholder = element.getAttribute('src')
    if (placeholder) element.dataset.placeholder = placeholder
  }

  if (sizes) element.setAttribute('sizes', sizes)
  if (srcset) element.setAttribute('srcset', srcset)
  if (src && element instanceof HTMLImageElement) element.setAttribute('src', src)

  element.setAttribute(ATTACHED_ATTRIBUTE, 'true')
}

/**
 * Attaches sources to every manually-deferred image inside `root`.
 * Safe to call repeatedly — already-attached images are skipped.
 */
export function loadDeferredImages(root: Root) {
  if (!root) return

  root.querySelectorAll<HTMLSourceElement>(DEFERRED_SOURCE_SELECTOR).forEach(attachSource)
  root.querySelectorAll<HTMLImageElement>(DEFERRED_IMAGE_SELECTOR).forEach(attachSource)
}

/**
 * Detaches sources again so the decoded bitmap can be released. Used for galleries that
 * would otherwise hold every slide decoded at once.
 */
export function unloadDeferredImages(root: Root) {
  if (!root) return

  root.querySelectorAll<HTMLSourceElement>(`source[${ATTACHED_ATTRIBUTE}]`).forEach((source) => {
    source.removeAttribute('srcset')
    source.removeAttribute('sizes')
    source.removeAttribute(ATTACHED_ATTRIBUTE)
  })

  root.querySelectorAll<HTMLImageElement>(`img[${ATTACHED_ATTRIBUTE}]`).forEach((image) => {
    image.removeAttribute('srcset')
    image.removeAttribute('sizes')

    const placeholder = image.dataset.placeholder
    if (placeholder) image.setAttribute('src', placeholder)

    image.removeAttribute(ATTACHED_ATTRIBUTE)
  })
}
